/* eslint-disable-next-line @typescript-eslint/no-restricted-imports -- (https://aka.ms/OWALintWiki)
 * BASELINE. Do not copy and paste!
 *	> 'dexie' import is restricted from being used. Importing from dexie is allowed but needs an additional approver */
import Dexie, { type Table, type IndexableType } from "dexie";
import type { UserEmailAddress } from "accelerator-aad/lib/UserEmailAddress";
import { logGreyError } from "owa-analytics";
import { getModuleContextMailboxInfo } from "owa-module-context-mailboxinfo";
import type { OwaDate } from "owa-datetime";
import type { Building, CollaboratorGroup, WorkLocationType } from "owa-graph-schema";

const INDEXED_DB_NAME = "placesCache";
const TABLE_NAME = "placesPeople";

export interface PeopleCacheInfo {
    uniqueId: string;
    owner: string;
    startOfDay: number;
    location: Building["id"] | WorkLocationType;
    email: UserEmailAddress;
    collaboratorType: CollaboratorGroup | "Meeting";
    expirationTime: number;
}

export interface PeopleInfo {
    date: OwaDate;
    location: Building["id"] | WorkLocationType | "Error";
    email: UserEmailAddress;
    collaboratorType: CollaboratorGroup | "Meeting";
}

// Collaborator Cache
export interface CollaboratorCache {
    uniqueId: UserEmailAddress;
    owner: string;
    orderIndex: number;
}

export interface WorkGroupCache {
    workGroupId: string;
    name: string;
    iconPath?: string;
    members?: UserEmailAddress[];
}

export interface InternalWorkGroupCache extends WorkGroupCache {
    owner: string;
}
export interface PeopleTimezoneSettingsCache {
    uniqueId: string; // owner
    timezone: string;
}

// TODO: Add expiration date logic
// WI: https://outlookweb.visualstudio.com/MicrosoftPlaces/_workitems/edit/268634
// TODO: Add in fixed types to other caches
/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * Baseline. DO NOT COPY AND PASTE!, replace this line with a justification or fix the issue.
 *	> Function Dexie called at module scope is not allowed. This may cause unwanted side effects. */
export const db: Dexie = new Dexie(INDEXED_DB_NAME);
/** uniqueId is the primary key
 * we want the uniqueId to be a combination of email, location, and collaboratorType
 * so we can easily query/put to cache */

/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function stores called at module scope is not allowed. This may cause unwanted side effects.
 *	> Function version called at module scope is not allowed. This may cause unwanted side effects. */
db.version(5).stores({
    placesPeople: "uniqueId,location,collaboratorType,startOfDay,owner",
    placesSavedCollaborators: "uniqueId,owner",
    placesSuggestedCollaborators: "uniqueId,owner",
    placesWorkGroups: "workGroupId,owner,workGroupId",
    placesPeopleTimezoneSettings: "uniqueId,timezone",
    placesUpsPeopleTable: null, // the one that added null value is 5, on the next version, remove this table.
});

/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function table called at module scope is not allowed. This may cause unwanted side effects. */
export const placesPeopleTable: Table<any, IndexableType, any> = db.table(TABLE_NAME);

// Saved Collaborator Table
/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function table called at module scope is not allowed. This may cause unwanted side effects. */
export const savedCollaboratorTable: Table<any, IndexableType, any> = db.table("placesSavedCollaborators");

// Suggested Collaborator Table
/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function table called at module scope is not allowed. This may cause unwanted side effects. */
export const suggestedCollaboratorTable: Table<any, IndexableType, any> = db.table("placesSuggestedCollaborators");

// Work Groups Table
export const workGroupsTable: Table<InternalWorkGroupCache, string, InternalWorkGroupCache> = 
/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function table called at module scope is not allowed. This may cause unwanted side effects. */
db.table<InternalWorkGroupCache, string>("placesWorkGroups");

// Cached timezone the people info is stored in
/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function table called at module scope is not allowed. This may cause unwanted side effects. */
export const peopleTimezoneSettingsTable: Table<any, IndexableType, any> = db.table("placesPeopleTimezoneSettings");

export async function deletePlacesCacheDatabase(): Promise<void> {
    if (await Dexie.exists(INDEXED_DB_NAME)) {
        try {
            if (db.isOpen()) {
                db.close(); // Have to close the database before deleting, otherwise InvalidStateError will be thrown.
            }
        }
        catch (err) {
            // Errors resulting from enabling useUnknownInCatchVariables
            // TS2345: Argument of type 'unknown' is not assignable to parameter of type 'Error | undefined'.
            // @ts-expect-error
            logGreyError("PlacesCloseDatabaseError", getModuleContextMailboxInfo(), err, {
                dbName: INDEXED_DB_NAME,
            });
        }
        await Dexie.delete(INDEXED_DB_NAME);
    }
}
